import React from 'react';
import Modal from 'react-bootstrap/Modal';
import {css} from 'aphrodite';
import {renderMultifieldString} from 'libs/salmon-utils';
import Markdown from 'components/Markdown';

/**
 * New Wrapper component to handle state for the coverage summary modal
 */
export const CoverageSummaryModal = (props) => {
  const {
    column,
    rowData,
    styleSheet,
    showSummaryModal,
    handleHide,
  } = props;

  if (!rowData) {
    return null;
  }

  const displayValueMap = column.meta?.displayValueMap;

  return (
    <Modal
      show={showSummaryModal}
      onHide={handleHide}
      aria-labelledby={'summary-modal-title'}
      backdrop={true}
      size='lg'
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName={`pal-modal ${css(styleSheet.coverageSummaryModal)}`}
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton>
        <Modal.Title id={'summary-modal-title'} className={'summary-modal-title'}>
          {column.meta && column.meta.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'summary-modal-body'}>
        <div>
          <div style={{marginBottom: '15px'}}>
            <label className={css(styleSheet.coverageSummaryModalLabel)}>
              <span>{renderMultifieldString(column.meta?.subtitle, rowData)}</span>
            </label>
          </div>
          <div className={'markdown-template'}>
            <Markdown>
              {column.meta?.template
                ? renderTemplateWithPlaceholders(column.meta.template, rowData, displayValueMap)
                : rowData[column.property] ?? ''}
            </Markdown>
          </div>
        </div>
      </Modal.Body>
      {column.meta?.disclaimer &&
        <Modal.Footer className={css(styleSheet.coverageSummaryModalFooter)}>
          <Markdown>{column.meta?.disclaimer}</Markdown>
        </Modal.Footer>
      }
    </Modal>
  );
};

/**
 * Applies column.meta.displayValueMap to raw field values shown in the summary template.
 * Strings are replaced only on exact key match; arrays are mapped element-wise recursively.
 * If there is no map, or the value is not a mapped string, it is returned unchanged.
 */
const mapDisplayValue = (value, displayValueMap) => {
  if (!displayValueMap) {
    return value;
  }
  // displayValueMap keys match the stored value exactly (e.g. code → human-readable label).
  if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(displayValueMap, value)) {
    return displayValueMap[value];
  }
  // Multi-value fields: map each item so list items get the same label treatment.
  if (Array.isArray(value)) {
    return value.map((item) => mapDisplayValue(item, displayValueMap));
  }
  return value;
};

/**
 * Gets a field value from rowData, checking both direct properties and the fields array.
 * Used for template placeholders when data may come from rowData.fields (e.g. dynamic columns).
 * displayValueMap: optional exact string key → replacement from column.meta.displayValueMap (JSON style).
 */
const getFieldValueFromRowData = (rowData, key, displayValueMap) => {
  if (!rowData) {
    return '';
  }

  let raw;
  if (rowData.hasOwnProperty(key) && rowData[key] !== undefined && rowData[key] !== null) {
    raw = rowData[key];
  } else if (Array.isArray(rowData.fields)) {
    const field = rowData.fields.find((f) => f && f.fieldName === key);
    if (field && field.value !== undefined && field.value !== null) {
      raw = field.value;
    }
  }

  if (raw === undefined) {
    return '';
  }
  return mapDisplayValue(raw, displayValueMap);
};

/**
 * Renders a template string by replacing [[fieldName]] placeholders with rowData values.
 * Supports both direct properties and rowData.fields array. Result is suitable for markdown.
 */
const renderTemplateWithPlaceholders = (template, rowData, displayValueMap) => {
  if (!template || typeof template !== 'string') {
    return '';
  }

  // Each [[fieldName]] token is replaced with the value from rowData (and displayValueMap).
  return template.replace(/\[\[(.*?)\]\]/g, (match, key) => {
    let foundValue = getFieldValueFromRowData(rowData, key, displayValueMap);
    if (foundValue && Array.isArray(foundValue)) {
      return foundValue.sort().join(', ');
    }
    return foundValue ?? '';
  });
};
